<script setup>
import { ref, onMounted, nextTick, watch } from "vue";
import { isEmpty, has, get } from "lodash";
import { initSelect2 } from "../helpers/select2.js";

const props = defineProps({
    id: String,
    title: String,
    name: String,
    required: Boolean,
    data: {
        type: Array,
        default: () => []
    },
});

const repeaterId = `id-v-repeater${props.id ? '-' + props.id : ''}`;

const rows = ref(props.data && props.data.length > 0 ? props.data : [{ id: 1 }]);

onMounted(() => {
    initializeInputs();
});

const addRow = async () => {
    rows.value.push({id: rows.value.length + 1});
    await nextTick();
    initializeInputs();
};

const deleteRow = async (index) => {
    rows.value.splice(index, 1);
    await nextTick();
    initializeInputs();
};

const initializeInputs = () => {
    const tableRows = document.querySelectorAll(`#${repeaterId} table tbody tr`);
    let shouldInitSelect2 = false;

    tableRows.forEach((row, index) => {
        row.querySelector('.v-repeater-row-index').text = index;

        const inputs = row.querySelectorAll('input, select');

        inputs.forEach((input) => {
            let baseName = input.getAttribute('data-input-name');
            if (!isEmpty(baseName)) {
                let name = `${props.name}[${index}][${baseName}]`;
                let id = `id-${baseName}[${index}]`;

                if (input.nodeName === 'SELECT' && input.hasAttribute('multiple')) {
                    name = name + '[]';
                }

                input.setAttribute('name', name);
                input.setAttribute('id', id);

                if (!isEmpty(props.data) && !isEmpty(props.data[index]) && has(props.data[index], baseName) && !isEmpty(get(props.data[index], baseName))) {
                    $(input).val(get(props.data[index], baseName)).change();
                }

                if (input.hasAttribute('data-toggle') && !shouldInitSelect2) {
                    shouldInitSelect2 = true;
                }
            }
        });
    });

    if (shouldInitSelect2) {
        initSelect2();
    }
};

// Expose methods to parent if needed
defineExpose({
    addRow,
    deleteRow,
    rows
});
</script>

<template>
    <div class="v-repeater" :id="repeaterId">
        <div class="flex justify-between items-center mb-2">
            <label class="form-label font-normal text-gray-900 required flex-row items-center">
                <span v-if="title">{{ title }}</span>
                <span v-if="required" class="text-danger">*</span>
            </label>
            <button
                type="button"
                class="btn btn-icon btn-sm btn-light-primary"
                @click="addRow"
            >
                <i class="ki-filled ki-plus-squared"></i>
            </button>
        </div>

        <table class="table table-primary table-auto table-border v-repeater-table">
            <thead>
                <tr>
                    <th class="text-center">#</th>
                    <slot name="th"></slot>
                    <th class="text-center">Action</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, index) in rows" :key="row.id">
                    <td class="text-center v-repeater-row-index">{{ index + 1 }}</td>
                    <slot name="td" :row="row" :index="index"></slot>
                    <td class="text-center">
                        <button
                            type="button"
                            class="btn btn-outline btn-danger btn-xs"
                            @click="deleteRow(index)"
                        >
                            <i class="ki-outline ki-trash"></i>
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.v-repeater {
    @apply w-full;
}

.v-repeater-table {
    @apply w-full;
}

.v-repeater-table th,
.v-repeater-table td {
    @apply p-2;
}

.btn-icon {
    @apply p-2 rounded-full hover:btn-primary;
}
</style>
